import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {TextSpellcheckOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-spellcheck-option';
import {SpellcheckContextualItems} from '@Components/poster-editor/components/poster-editing-side-panel/components/spellcheck-contextual-items';
import {ContextualMenu} from '@Components/contextual-menu';
import {updateOptionsPanelHeight, updateOptionsPanelProperties} from '@Components/poster-editor/library/poster-editor-library';
import {updateSpellCheckSettingsState} from '@Components/poster-editor/poster-editor-reducer';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

export interface PosterEditorItemSpellcheckPanelProps {
  showListOfIgnoredWords?: boolean;
  panelId: string;
}

enum PanelToShow {
  DEFAULT = 'DEFAULT',
  IGNORED_WORDS = 'IGNORED_WORDS',
}

export default function PosterEditorItemSpellcheckPanel({showListOfIgnoredWords = false, ...props}: PosterEditorItemSpellcheckPanelProps): ReactElement {
  const [textRef, setTextRef] = useState<HTMLDivElement | null>(null);
  const [ignoredWordsCount, setIgnoredWordsCount] = useState(-1);
  const onClose = useDefaultPanelClose(props.panelId);
  const [contentToShow, setContentToShow] = useState<PanelToShow>(showListOfIgnoredWords ? PanelToShow.IGNORED_WORDS : PanelToShow.DEFAULT);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useEffect(() => {
    if (contentToShow === PanelToShow.DEFAULT) {
      const height = 170;
      const hasSnapPoints = false;

      updateOptionsPanelHeight(props.panelId, `${height}px`);
      updateOptionsPanelProperties(props.panelId, hasSnapPoints);
      setIgnoredWordsCount(-1);
    }
  }, [contentToShow]);

  useEffect(() => {
    if (ignoredWordsCount === 0) {
      updateOptionsPanelHeight(props.panelId, `160px`);
      updateOptionsPanelProperties(props.panelId, false);
    } else {
      if (!textRef) {
        return;
      }

      const height = textRef.clientHeight + 80;
      const hasSnapPoints = true;

      updateOptionsPanelHeight(props.panelId, `${height}px`);
      updateOptionsPanelProperties(props.panelId, hasSnapPoints);
    }
  }, [ignoredWordsCount]);

  const handleOnClose = (): void => {
    onClose();
    window.PMW.redux.store.dispatch(updateSpellCheckSettingsState(false));
  };

  const onIgnoredWordsLoaded = (count: number): void => {
    setIgnoredWordsCount(count);
  };

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        onBackButton={(): void => {
          setContentToShow(PanelToShow.DEFAULT);
        }}
        showBackButton={contentToShow === PanelToShow.IGNORED_WORDS}
        onClose={handleOnClose}
        title={contentToShow === PanelToShow.IGNORED_WORDS ? window.i18next.t('pmwjs_ignored_words') : window.i18next.t('pmwjs_spellcheck')}
      />
    );
  };

  const getListOfIgnoredWordsContent = (): ReactElement => {
    return (
      <ContextualMenu includeLeftRightPadding={false} id="spellcheck-contextual-options" contentClassName="spacing-p-t-1 spacing-p-l-4 spacing-p-r-4 spacing-p-b-4">
        <div ref={setTextRef}>
          <SpellcheckContextualItems onWordsLoaded={onIgnoredWordsLoaded} showListOfIgnoredWordsOnly />
        </div>
      </ContextualMenu>
    );
  };

  const getContent = (): ReactElement => {
    if (contentToShow === PanelToShow.IGNORED_WORDS) {
      return getListOfIgnoredWordsContent();
    }
    return (
      <ContextualMenu id="spellcheck-mobile-options" contentClassName="spacing-p-t-1 spacing-p-l-4 spacing-p-r-4" includeLeftRightPadding={false}>
        {activeItem && (activeItem.isText() || (activeItem.isSlideshow() && activeItem.getSelectedSlide().isTextSlide())) ? (
          <div>
            <TextSpellcheckOption />
          </div>
        ) : null}
        <ControlledList
          className="spacing-m-0"
          items={[
            {
              onClick(): void {
                setContentToShow(PanelToShow.IGNORED_WORDS);
              },
              type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
              text: window.i18next.t('pmwjs_list_of_ignored_words'),
              icon: 'icon-list',
              id: 'list_of_ignored_words',
            },
          ]}
        />
      </ContextualMenu>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
